import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Camera,
  Lock,
  CreditCard,
  Bell,
  Mail,
  Monitor,
  Send,
  Users,
  CheckCircle,
  Palette,
  AlertCircle,
  User,
  Trash2,
  Loader,
  Check,
} from 'lucide-react';
import { useThemeStore, THEMES } from '../../store/themeStore';
import { billingAPI, getErrorMessage, notificationsAPI, usersAPI } from '../../services/api';

const BrandVoiceSection = lazy(() => import('./BrandVoiceSection'));

interface Subscription {
  plan_name: string;
  price: number;
  billing_cycle: string;
  current_period_end?: string;
}

interface UserProfile {
  id: string;
  name: string;
  email: string;
  avatar_url?: string;
  plan: string;
  created_at: string;
}

type NotificationCategory =
  | 'publishing'
  | 'team'
  | 'approval'
  | 'reminder'
  | 'billing'
  | 'promotion';

interface NotificationPreferences {
  email_enabled: boolean;
  in_app_enabled: boolean;
  email_categories: Record<NotificationCategory, boolean>;
}

interface NotificationPreferencesResponse {
  preferences: NotificationPreferences;
}

const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  'publishing',
  'team',
  'approval',
  'reminder',
  'billing',
  'promotion',
];

const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  email_enabled: true,
  in_app_enabled: true,
  email_categories: {
    publishing: true,
    team: true,
    approval: true,
    reminder: true,
    billing: true,
    promotion: false,
  },
};

const normalizeNotificationPreferences = (
  preferences?: Partial<NotificationPreferences>,
): NotificationPreferences => ({
  email_enabled: preferences?.email_enabled ?? DEFAULT_NOTIFICATION_PREFERENCES.email_enabled,
  in_app_enabled: preferences?.in_app_enabled ?? DEFAULT_NOTIFICATION_PREFERENCES.in_app_enabled,
  email_categories: NOTIFICATION_CATEGORIES.reduce(
    (acc, category) => ({
      ...acc,
      [category]:
        preferences?.email_categories?.[category] ??
        DEFAULT_NOTIFICATION_PREFERENCES.email_categories[category],
    }),
    {} as Record<NotificationCategory, boolean>,
  ),
});

// ── Reusable section shell ─────────────────────────────────────

function Section({
  icon: Icon,
  title,
  desc,
  danger = false,
  children,
}: {
  icon: React.ElementType;
  title: string;
  desc: string;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`n-card p-6 md:p-8 ${danger ? 'border-n-red/40' : ''}`}>
      {/* Header row */}
      <div className="flex items-center gap-3 mb-6 pb-5 border-b border-n-border">
        <div className={`w-9 h-9 rounded-n flex items-center justify-center flex-shrink-0 ${
          danger ? 'bg-n-red-bg' : 'bg-n-hover'
        }`}>
          <Icon className={`w-4.5 h-4.5 ${danger ? 'text-n-red' : 'text-n-text-md'}`} strokeWidth={1.8} />
        </div>
        <div>
          <h2 className={`text-[15px] font-semibold ${danger ? 'text-n-red' : 'text-n-text'}`}>{title}</h2>
          <p className="n-caption mt-0.5">{desc}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

// ── Notion-style text input ────────────────────────────────────

function Field({
  label,
  id,
  hint,
  children,
}: {
  label: string;
  id?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      {id ? (
        <label htmlFor={id} className="studio-section-label block mb-1.5">
          {label}
        </label>
      ) : (
        <p className="studio-section-label mb-1.5">{label}</p>
      )}
      {children}
      {hint && <p className="n-caption mt-1.5">{hint}</p>}
    </div>
  );
}

const inputCls =
  'w-full px-3 py-2.5 border border-n-border rounded-n bg-n-card text-[13px] text-n-text ' +
  'placeholder:text-n-text-lt focus:outline-none focus:ring-1 focus:ring-n-blue/20 focus:border-n-blue ' +
  'transition-all duration-75';

const inputDisabledCls =
  'w-full px-3 py-2.5 border border-n-border rounded-n bg-n-page text-[13px] text-n-text-md ' +
  'cursor-not-allowed select-none';

// ── Main component ─────────────────────────────────────────────

export const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme, themeId, setTheme } = useThemeStore();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>(
    DEFAULT_NOTIFICATION_PREFERENCES,
  );
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const queryClient = useQueryClient();

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), type === 'error' ? 5000 : 3000);
  };

  const { data: profile, isLoading } = useQuery<UserProfile>({
    queryKey: ['profile'],
    queryFn: () => usersAPI.getProfile().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const { data: subData, isLoading: isSubLoading } = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: () => billingAPI.subscription().then(r => r.data as Subscription),
  });

  const { data: notificationPreferencesData, isLoading: isNotificationPreferencesLoading } =
    useQuery<NotificationPreferencesResponse>({
      queryKey: ['notification-preferences'],
      queryFn: () =>
        notificationsAPI
          .getPreferences()
          .then(r => r.data as NotificationPreferencesResponse),
      staleTime: 5 * 60 * 1000,
    });

  useEffect(() => {
    if (profile) {
      setName(profile.name || '');
      setEmail(profile.email || '');
    }
  }, [profile]);

  useEffect(() => {
    if (notificationPreferencesData?.preferences) {
      setNotificationPreferences(
        normalizeNotificationPreferences(notificationPreferencesData.preferences),
      );
    }
  }, [notificationPreferencesData]);

  const updateProfileMutation = useMutation({
    mutationFn: (data: { name?: string; avatar_url?: string }) => usersAPI.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      showToast(t('settings_profile_updated', 'Profile updated successfully'), 'success');
    },
    onError: (err: unknown) => {
      showToast(
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          t('settings_profile_update_failed', 'Failed to update profile'),
        'error',
      );
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data: { current_password: string; new_password: string }) =>
      usersAPI.changePassword(data),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showToast(t('settings_password_changed', 'Password changed successfully'), 'success');
    },
    onError: (err: unknown) => {
      showToast(
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          t('settings_password_change_failed', 'Failed to change password'),
        'error',
      );
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => usersAPI.deleteAccount(),
    onSuccess: () => {
      showToast(t('settings_account_deactivated', 'Account deactivated'), 'success');
      setTimeout(() => { window.location.href = '/login'; }, 1500);
    },
    onError: (err: unknown) => {
      showToast(
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          t('settings_account_delete_failed', 'Failed to delete account'),
        'error',
      );
    },
  });

  const updateNotificationPreferencesMutation = useMutation({
    mutationFn: (preferences: NotificationPreferences) =>
      notificationsAPI.updatePreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      showToast(t('settings_notifications_saved', 'Notification preferences saved'), 'success');
    },
    onError: (err: unknown) => {
      if (notificationPreferencesData?.preferences) {
        setNotificationPreferences(
          normalizeNotificationPreferences(notificationPreferencesData.preferences),
        );
      }
      showToast(
        getErrorMessage(
          err,
          t('settings_notifications_save_failed', 'Failed to update notification preferences'),
        ),
        'error',
      );
    },
  });

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await updateProfileMutation.mutateAsync({ name: name.trim() });
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showToast(t('settings_passwords_no_match', 'Passwords do not match'), 'error');
      return;
    }
    if (newPassword.length < 6) {
      showToast(t('settings_password_min_length', 'New password must be at least 6 characters'), 'error');
      return;
    }
    await changePasswordMutation.mutateAsync({ current_password: currentPassword, new_password: newPassword });
  };

  const persistNotificationPreferences = (nextPreferences: NotificationPreferences) => {
    setNotificationPreferences(nextPreferences);
    updateNotificationPreferencesMutation.mutate(nextPreferences);
  };

  const handleNotificationChannelChange = (channel: 'email_enabled' | 'in_app_enabled') => {
    persistNotificationPreferences({
      ...notificationPreferences,
      [channel]: !notificationPreferences[channel],
    });
  };

  const handleNotificationCategoryChange = (category: NotificationCategory) => {
    persistNotificationPreferences({
      ...notificationPreferences,
      email_categories: {
        ...notificationPreferences.email_categories,
        [category]: !notificationPreferences.email_categories[category],
      },
    });
  };

  const subscription = subData || null;

  return (
    <div className="bg-n-page min-h-full">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 space-y-4">

        {/* ── Toast ──────────────────────────────────────────── */}
        {toast && (
          <div
            className={`fixed top-5 right-5 z-50 flex items-center gap-2.5 px-4 py-3 rounded-n
              text-[13px] font-medium shadow-n-sm border transition-all animate-in fade-in ${
              toast.type === 'error'
                ? 'bg-n-red-bg text-n-red border-n-red/30'
                : 'bg-n-green-bg text-n-green border-n-green/30'
            }`}
          >
            {toast.type === 'error' ? (
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
            ) : (
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
            )}
            {toast.msg}
          </div>
        )}

        {/* ── Skeleton ───────────────────────────────────────── */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="n-card p-6 animate-pulse">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 bg-n-hover rounded-n" />
                  <div className="space-y-1.5">
                    <div className="h-3.5 bg-n-hover rounded w-24" />
                    <div className="h-2.5 bg-n-hover rounded w-36" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-10 bg-n-hover rounded-n" />
                  <div className="h-10 bg-n-hover rounded-n" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">

            {/* ── Profile ──────────────────────────────────────── */}
            <Section
              icon={User}
              title={t('settings_profile_title', 'Profile')}
              desc={t('settings_profile_desc', 'Update your personal information')}
            >
              <form onSubmit={handleUpdateProfile} className="space-y-5">
                {/* Avatar row */}
                <div className="flex items-center gap-5">
                  {/* Avatar placeholder */}
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center
                      text-white font-bold text-2xl flex-shrink-0"
                    style={{ backgroundColor: theme.hex.primary }}
                  >
                    {(name || profile?.name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <button
                      type="button"
                      className="studio-toolbar-btn"
                    >
                      <Camera className="w-3.5 h-3.5" />
                      {t('settings_upload_picture_btn', 'Upload Picture')}
                    </button>
                    <p className="n-caption mt-1.5">
                      {t('settings_picture_size_hint', 'Recommended: 512×512px')}
                    </p>
                  </div>
                </div>

                <div className="border-t border-n-border pt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={t('settings_full_name_label', 'Full Name')} id="full-name">
                    <input
                      id="full-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={inputCls}
                      placeholder={t('settings_full_name_placeholder', 'Your name')}
                    />
                  </Field>

                  <Field
                    label={t('settings_email_address_label', 'Email Address')}
                    id="email-address"
                    hint={t('settings_email_cannot_change', 'Email cannot be changed')}
                  >
                    <input
                      id="email-address"
                      type="email"
                      value={email}
                      disabled
                      className={inputDisabledCls}
                      placeholder="your@email.com"
                    />
                  </Field>
                </div>

                {profile?.created_at && (
                  <p className="n-caption border-t border-n-border pt-4">
                    {t('settings_member_since', 'Member since')}{' '}
                    <span className="font-medium text-n-text-md">
                      {new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                  </p>
                )}

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={updateProfileMutation.isPending || name.trim() === (profile?.name || '')}
                    className="studio-toolbar-btn-primary h-8 px-4 text-[13px]"
                  >
                    {updateProfileMutation.isPending
                      ? t('settings_saving', 'Saving…')
                      : t('settings_save_changes_btn', 'Save Changes')}
                  </button>
                </div>
              </form>
            </Section>

            {/* ── Appearance ───────────────────────────────────── */}
            <Section
              icon={Palette}
              title={t('settings_appearance_title', 'Appearance')}
              desc={t('settings_appearance_desc', 'Customize your theme')}
            >
              <p className="n-caption mb-4">
                {t('settings_theme_preference', 'Choose your preferred color theme for the interface')}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {THEMES.map((th) => {
                  const isActive = themeId === th.id;
                  return (
                    <button
                      key={th.id}
                      onClick={() => setTheme(th.id)}
                      className={`group relative flex flex-col items-center gap-2.5 p-4 rounded-n border transition-all duration-75 ${
                        isActive
                          ? 'border-n-blue bg-n-blue-bg shadow-n-sm'
                          : 'border-n-border bg-n-card hover:border-n-blue/40 hover:bg-n-hover'
                      }`}
                    >
                      {/* Colour swatch */}
                      <div
                        className="w-10 h-10 rounded-n-sm"
                        style={{ backgroundColor: th.hex.primary }}
                      />
                      <p className={`text-xs font-medium ${isActive ? 'text-n-blue' : 'text-n-text-md'}`}>
                        {th.name}
                      </p>
                      {/* Active tick */}
                      {isActive && (
                        <span className="absolute top-2 right-2 w-4 h-4 bg-n-blue rounded-full flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </Section>

            {/* ── Security / Password ──────────────────────────── */}
            <Section
              icon={Lock}
              title={t('settings_security_title', 'Security')}
              desc={t('settings_security_desc', 'Change your password')}
            >
              <form onSubmit={handleChangePassword} className="space-y-4">
                <Field label={t('settings_current_password_label', 'Current Password')} id="current-password">
                  <input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className={inputCls}
                    placeholder={t('settings_current_password_placeholder', 'Enter your current password')}
                    required
                  />
                </Field>

                <div className="border-t border-n-border pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={t('settings_new_password_label', 'New Password')} id="new-password">
                    <input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className={inputCls}
                      placeholder={t('settings_new_password_placeholder', 'Min. 6 characters')}
                      required
                      minLength={6}
                    />
                  </Field>

                  <Field label={t('settings_confirm_password_label', 'Confirm Password')} id="confirm-password">
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={inputCls}
                      placeholder={t('settings_confirm_password_placeholder', 'Confirm your password')}
                      required
                    />
                  </Field>
                </div>

                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-[12px] text-n-red flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {t('settings_passwords_no_match', 'Passwords do not match')}
                  </p>
                )}

                <div className="flex justify-end pt-2 border-t border-n-border">
                  <button
                    type="submit"
                    disabled={changePasswordMutation.isPending || !currentPassword || !newPassword || !confirmPassword}
                    className="studio-toolbar-btn-primary h-8 px-4 text-[13px]"
                  >
                    {changePasswordMutation.isPending
                      ? t('settings_updating', 'Updating…')
                      : t('settings_change_password_btn', 'Change Password')}
                  </button>
                </div>
              </form>
            </Section>

            {/* ── Plan & Billing ───────────────────────────────── */}
            <Section
              icon={CreditCard}
              title={t('settings_plan_billing_title', 'Plan & Billing')}
              desc={t('settings_plan_billing_desc', 'Manage your subscription')}
            >
              {isSubLoading ? (
                <div className="animate-pulse h-16 bg-n-hover rounded-n" />
              ) : subscription ? (
                <div className="border border-n-border rounded-n p-5 flex items-start justify-between gap-4">
                  <div>
                    <p className="n-caption mb-1">{t('settings_current_plan_label', 'Current Plan')}</p>
                    <p className="text-[15px] font-bold text-n-text">{subscription.plan_name}</p>
                    {subscription.price > 0 && (
                      <p className="n-caption mt-1">
                        <span className="font-semibold text-n-text-md">${subscription.price}</span>
                        /{subscription.billing_cycle === 'yearly'
                          ? t('settings_year', 'year')
                          : t('settings_month', 'month')}
                      </p>
                    )}
                    {subscription.current_period_end && (
                      <p className="n-caption mt-3 pt-3 border-t border-n-border">
                        {t('settings_next_billing_date', 'Next billing date:')}{' '}
                        <span className="font-medium text-n-text-md">
                          {new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                            month: 'long', day: 'numeric', year: 'numeric',
                          })}
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-3 flex-shrink-0">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-n-green-bg text-n-green">
                      <span className="w-1.5 h-1.5 rounded-full bg-n-green" />
                      {t('settings_active_status', 'Active')}
                    </span>
                    <Link
                      to="/billing"
                      className="studio-toolbar-btn-primary h-8 px-4 text-[13px] no-underline"
                    >
                      {t('settings_manage_billing_btn', 'Manage Billing')}
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="border border-n-blue/20 rounded-n bg-n-blue-bg/30 p-5">
                  <p className="text-[13px] text-n-text-md mb-4">
                    {t('settings_no_subscription', "You don't have an active subscription yet. Explore our plans to unlock more features.")}
                  </p>
                  <Link
                    to="/billing"
                    className="studio-toolbar-btn-primary h-8 px-4 text-[13px] no-underline"
                  >
                    {t('settings_view_plans_btn', 'View Plans')}
                  </Link>
                </div>
              )}
            </Section>

            {/* ── Notifications ────────────────────────────────── */}
            <Section
              icon={Bell}
              title={t('settings_notifications_title', 'Notifications')}
              desc={t('settings_notifications_desc', 'Manage your notification preferences')}
            >
              <div className="space-y-5">
                <div>
                  <p className="studio-section-label mb-2">
                    {t('settings_notifications_delivery_label', 'Delivery')}
                  </p>
                  <div className="grid gap-2 md:grid-cols-2">
                    {[
                      {
                        key: 'email_enabled' as const,
                        icon: Mail,
                        label: t('settings_notifications_email_channel', 'Email delivery'),
                        desc: t(
                          'settings_notifications_email_channel_desc',
                          'Send important alerts and summaries to your inbox.',
                        ),
                      },
                      {
                        key: 'in_app_enabled' as const,
                        icon: Monitor,
                        label: t('settings_notifications_in_app_channel', 'In-app center'),
                        desc: t(
                          'settings_notifications_in_app_channel_desc',
                          'Show alerts in the notification bell while you work.',
                        ),
                      },
                    ].map(({ key, icon: Icon, label, desc }) => {
                      const checked = notificationPreferences[key];
                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between gap-4 rounded-n border border-n-border bg-n-card p-3"
                        >
                          <div className="flex min-w-0 items-start gap-3">
                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-n-sm bg-n-green-bg text-n-green">
                              <Icon className="h-4 w-4" strokeWidth={1.8} />
                            </span>
                            <div className="min-w-0">
                              <p className="text-[13px] font-semibold text-n-text">{label}</p>
                              <p className="n-caption mt-0.5 leading-relaxed">{desc}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={checked}
                            aria-label={label}
                            disabled={
                              isNotificationPreferencesLoading ||
                              updateNotificationPreferencesMutation.isPending
                            }
                            onClick={() => handleNotificationChannelChange(key)}
                            className={`relative h-6 w-11 flex-shrink-0 rounded-full border transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-n-blue/20 ${
                              checked
                                ? 'border-n-green bg-n-green'
                                : 'border-n-border bg-n-hover'
                            }`}
                          >
                            <span
                              className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-150 ${
                                checked ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <p className="studio-section-label">
                      {t('settings_notifications_categories_label', 'Notify me about')}
                    </p>
                    {updateNotificationPreferencesMutation.isPending && (
                      <span className="n-caption">
                        {t('settings_notifications_saving', 'Saving...')}
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {[
                      {
                        key: 'publishing' as const,
                        icon: Send,
                        label: t('settings_notifications_publishing', 'Publishing alerts'),
                        desc: t(
                          'settings_notifications_publishing_desc',
                          'Post published, failed, scheduled, or needs attention.',
                        ),
                      },
                      {
                        key: 'team' as const,
                        icon: Users,
                        label: t('settings_notifications_team', 'Team activity'),
                        desc: t(
                          'settings_notifications_team_desc',
                          'Invites, member updates, and workspace collaboration.',
                        ),
                      },
                      {
                        key: 'approval' as const,
                        icon: Check,
                        label: t('settings_notifications_approval', 'Approvals'),
                        desc: t(
                          'settings_notifications_approval_desc',
                          'Review requests and approval decisions.',
                        ),
                      },
                      {
                        key: 'reminder' as const,
                        icon: Bell,
                        label: t('settings_notifications_reminder', 'Reminders'),
                        desc: t(
                          'settings_notifications_reminder_desc',
                          'Scheduled post reminders and follow-ups.',
                        ),
                      },
                      {
                        key: 'billing' as const,
                        icon: CreditCard,
                        label: t('settings_notifications_billing', 'Billing and usage'),
                        desc: t(
                          'settings_notifications_billing_desc',
                          'Plan limits, invoices, and AI credit usage.',
                        ),
                      },
                      {
                        key: 'promotion' as const,
                        icon: Palette,
                        label: t('settings_notifications_promotion', 'Product updates'),
                        desc: t(
                          'settings_notifications_promotion_desc',
                          'Feature announcements, tips, and launch notes.',
                        ),
                      },
                    ].map(({ key, icon: Icon, label, desc }) => {
                      const checked = notificationPreferences.email_categories[key];
                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between gap-4 rounded-n border border-transparent px-3 py-3 transition-colors duration-75 hover:border-n-border hover:bg-n-hover"
                        >
                          <div className="flex min-w-0 items-start gap-3">
                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-n-sm bg-n-hover text-n-text-md">
                              <Icon className="h-4 w-4" strokeWidth={1.8} />
                            </span>
                            <div className="min-w-0">
                              <p className="text-[13px] font-semibold text-n-text">{label}</p>
                              <p className="n-caption mt-0.5">{desc}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={checked}
                            aria-label={label}
                            disabled={
                              isNotificationPreferencesLoading ||
                              updateNotificationPreferencesMutation.isPending
                            }
                            onClick={() => handleNotificationCategoryChange(key)}
                            className={`relative h-6 w-11 flex-shrink-0 rounded-full border transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-n-blue/20 ${
                              checked
                                ? 'border-n-green bg-n-green'
                                : 'border-n-border bg-n-hover'
                            }`}
                          >
                            <span
                              className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-150 ${
                                checked ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Section>

            {/* ── Brand Voice ──────────────────────────────────── */}
            <Suspense fallback={
              <div className="n-card p-6 flex justify-center items-center h-32">
                <Loader className="w-5 h-5 animate-spin text-n-text-lt" />
              </div>
            }>
              <BrandVoiceSection />
            </Suspense>

            {/* ── Danger Zone ──────────────────────────────────── */}
            <Section
              icon={Trash2}
              title={t('settings_danger_zone_title', 'Danger Zone')}
              desc={t('settings_danger_zone_desc', 'Irreversible account actions')}
              danger
            >
              {!showDeleteConfirm ? (
                <>
                  <p className="text-[13px] text-n-text-md mb-5">
                    {t('settings_delete_account_warning', 'Once you delete your account, there is no going back. All data will be permanently removed. Please be absolutely certain before proceeding.')}
                  </p>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="h-8 px-4 rounded-n-sm text-[13px] font-medium bg-n-red text-white
                      hover:opacity-90 transition-all duration-75"
                  >
                    {t('settings_delete_account_btn', 'Delete My Account')}
                  </button>
                </>
              ) : (
                <div className="border border-n-red/30 rounded-n bg-n-red-bg/40 p-5">
                  <div className="flex items-start gap-3 mb-5">
                    <AlertCircle className="w-5 h-5 text-n-red flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[13px] font-semibold text-n-red mb-1">
                        {t('settings_confirm_delete_title', 'Confirm Account Deletion')}
                      </p>
                      <p className="text-[13px] text-n-text-md">
                        {t('settings_confirm_delete_message', 'This action is permanent and cannot be undone. All your data, accounts, and settings will be deleted immediately.')}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-4 border-t border-n-red/20">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="studio-toolbar-btn h-8 px-4 text-[13px]"
                    >
                      {t('settings_cancel_btn', 'Cancel')}
                    </button>
                    <button
                      onClick={() => deleteAccountMutation.mutate()}
                      disabled={deleteAccountMutation.isPending}
                      className="h-8 px-4 rounded-n-sm text-[13px] font-medium bg-n-red text-white
                        hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-75"
                    >
                      {deleteAccountMutation.isPending
                        ? t('settings_deleting', 'Deleting…')
                        : t('settings_confirm_delete_btn', 'Yes, Delete My Account')}
                    </button>
                  </div>
                </div>
              )}
            </Section>

          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
